import { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { HorizontalLocalMenu, Tab } from '@praktio/common-ui';

import Report from '../../common/Report/Report';
import PageHeader from '../../common/PageHeader/PageHeader';
import TableSummaryReport from './TableSummaryReport/TableSummaryReport';
import ReportSettingsPane from './ReportSettingsPane/ReportSettingsPane';
import OverlayProgressBar from '../../common/OverlayProgressBar/OverlayProgressBar';
import ReportNotLoaded from '../../common/ReportNotLoaded/ReportNotLoaded';
import ReportAccessError from '../../common/ReportAccessError/ReportAccessError';

import {
  clearReportData,
  fetchGeneralReportBatch,
  selectCanRegenerateReport,
} from '../../../ducks/report';
import { getTeams } from '../../../ducks/teams';
import { RootState } from '../../../ducks/store';
import { useGetTeamsSlice } from '../../../hooks/useGetTeamsSlice';
import { useFetchCoursesOnLoad } from '../../../hooks/useFetchCoursesOnLoad';
import { useBatchedReport, BatchFetchFn } from '../../../hooks/useBatchedReport';
import { useOverlayTransition } from '../../../hooks/useOverlayTransition';

import './GeneralReport.scss';

const GeneralReport = () => {
  // ===========================================================================
  // Redux
  // ===========================================================================

  const dispatch = useDispatch();

  const {
    activeTeams,
    activeDeselectedCourses,
    reportSettingsPaneOpen,
    teams,
    user,
    batchIsLoading,
    batchProgress,
    batchError,
    batchCanRetry,
    runRequestId,
    data,
  } = useSelector((state: RootState) => ({
    activeTeams: state.teamsSlice.activeTeams,
    activeDeselectedCourses: state.teamsSlice.activeDeselectedCourses,
    reportSettingsPaneOpen: state.report.reportSettingsPaneOpen,
    teams: state.teams.list,
    user: state.auth.authenticatedUser,
    batchIsLoading: state.report.batchIsLoading,
    batchProgress: state.report.batchProgress,
    batchError: state.report.batchError,
    batchCanRetry: state.report.batchCanRetry,
    runRequestId: state.report.runRequestId,
    data: state.report.data,
  }));

  const canRegenerate = useSelector(selectCanRegenerateReport);

  // ===========================================================================
  // Hooks
  // ===========================================================================

  useFetchCoursesOnLoad();

  const { noCoursesSelected } = useGetTeamsSlice();

  // ===========================================================================
  // Batched Report
  // ===========================================================================

  const fetchFn: BatchFetchFn = useCallback(
    async (teamId: string, signal: AbortSignal) => {
      return fetchGeneralReportBatch(teamId, activeDeselectedCourses, signal);
    },
    [activeDeselectedCourses],
  );

  const { trigger, abort, cancel } = useBatchedReport({
    batchKeys: activeTeams,
    fetchFn,
  });

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  useEffect(() => {
    dispatch(getTeams());
  }, [dispatch]);

  useEffect(() => {
    if (noCoursesSelected) {
      dispatch(clearReportData());
      return;
    }
  }, [noCoursesSelected, dispatch]);

  // Runs the report: on arrival, whenever the committed selection changes, and whenever the user
  // presses Run. runRequestId covers the last of those on its own, so pressing Run with an
  // unchanged selection still loads the report.
  useEffect(() => {
    if (noCoursesSelected) return;

    abort(); // Cancel any in-flight batch
    trigger();
  }, [noCoursesSelected, activeTeams, activeDeselectedCourses, runRequestId, trigger, abort]);

  const {
    showOverlay,
    isComplete,
    handleComplete: handleOverlayComplete,
  } = useOverlayTransition(batchIsLoading, canRegenerate);

  // Absent, not falsy: a failure carrying an empty message is still a failure, and treating it as
  // one keeps an errored run from being announced as politely as a cancellation the user chose.
  const hasError = batchError != null;

  // A run that ended without data. Which of the two messages the canvas shows turns on batchCanRetry:
  // a cancel and an ordinary transport failure get the not-loaded canvas, while a lapsed session,
  // lost access, and anything the classifier could not place get told to sign in rather than sent to
  // a Run button that will fail the same way. See canRetryAfter for what actually lands where.
  const showNotLoaded = !showOverlay && canRegenerate && batchCanRetry;
  const showAccessError = !showOverlay && canRegenerate && !batchCanRetry;
  const showReportData = !showOverlay && !canRegenerate;

  return (
    <div className="GeneralReport d-flex flex-column">
      <Helmet>
        <title>Reports - Praktio</title>
      </Helmet>

      <PageHeader pageName="Reports" className="mb-0" />

      <div className="GeneralReport_MainContainer">
        <ReportSettingsPane teams={teams} />

        <HorizontalLocalMenu
          classes={classNames({
            'HorizontalLocalMenu--open': reportSettingsPaneOpen,
          })}
          defaultActiveKey="summaryView"
        >
          <Tab eventKey="summaryView" title="Summary View">
            {showOverlay ? (
              <OverlayProgressBar
                progress={batchProgress}
                isComplete={isComplete}
                onCancel={cancel}
                onComplete={handleOverlayComplete}
              />
            ) : null}

            {showNotLoaded ? <ReportNotLoaded hasError={hasError} /> : null}

            {showAccessError ? <ReportAccessError /> : null}

            {showReportData ? <TableSummaryReport reportData={data} /> : null}
          </Tab>

          <Tab eventKey="detailView" title="Detail View">
            {showOverlay ? (
              <OverlayProgressBar
                progress={batchProgress}
                isComplete={isComplete}
                onCancel={cancel}
                onComplete={handleOverlayComplete}
              />
            ) : null}

            {showNotLoaded ? <ReportNotLoaded hasError={hasError} /> : null}

            {showAccessError ? <ReportAccessError /> : null}

            {showReportData ? (
              <Report user={user} reportData={data} teams={teams} isGeneral />
            ) : null}
          </Tab>
        </HorizontalLocalMenu>
      </div>
    </div>
  );
};

export default GeneralReport;
